
import { useState, useCallback, memo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ReviewCard } from "@/components/ReviewCard";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import type { Review } from "@/types/review";
import { Skeleton } from "@/components/ui/skeleton";

// Memoized ReviewCard component to prevent unnecessary re-renders
const MemoizedReviewCard = memo(ReviewCard);

// Create a loading skeleton specifically for review cards
const ReviewCardSkeleton = () => {
  return (
    <div className="p-4 h-[280px] flex flex-col border rounded-lg shadow-sm bg-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex gap-1">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <Skeleton className="h-8 w-8 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex justify-between">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="flex justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-16 w-full mt-2" />
        <Skeleton className="h-3 w-20 mt-auto" />
      </div>
    </div>
  );
};

export const RecentReviewsSection = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  
  // Increased to show 9 reviews instead of 6
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ['recentReviews'],
    queryFn: async () => {
      try {
        // Fetch reviews with a more efficient query
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select(`
            id,
            airport_name,
            airport_location,
            review_text,
            film_friendly_score,
            hand_check_success,
            created_at,
            user_id,
            profiles(username, avatar_url, verification_status, is_creative)
          `)
          .order('created_at', { ascending: false })
          .limit(9); // Increased from 6 to 9 reviews
        
        if (reviewsError) throw reviewsError;
        
        if (!reviewsData || reviewsData.length === 0) {
          return [];
        }
        
        // Process vote counts in parallel using Promise.all
        const reviewsWithVotes = await Promise.all(reviewsData.map(async (review) => {
          // Get upvotes count
          const { count: upvoteCount, error: upvoteError } = await supabase
            .from('review_votes')
            .select('*', { count: 'exact', head: true })
            .eq('review_id', review.id)
            .eq('vote_type', 'upvote');
          
          // Get downvotes count
          const { count: downvoteCount, error: downvoteError } = await supabase
            .from('review_votes')
            .select('*', { count: 'exact', head: true })
            .eq('review_id', review.id)
            .eq('vote_type', 'downvote');
            
          return {
            ...review,
            upvotes: upvoteCount || 0,
            downvotes: downvoteCount || 0
          } as Review;
        }));
        
        return reviewsWithVotes;
      } catch (error) {
        console.error("Error fetching reviews with votes:", error);
        return [];
      }
    },
    staleTime: 60000, // Cache data for 1 minute
    refetchOnWindowFocus: false
  });

  return (
    <section className="mb-8">
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-2xl font-semibold">{t('recent_reviews')}</h2>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="hover:bg-transparent p-0">
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent className="space-y-4">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {Array(9).fill(0).map((_, i) => (
                  <ReviewCardSkeleton key={i} />
                ))}
              </motion.div>
            ) : reviews.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="text-center py-8"
              >
                <p className="text-muted-foreground">{t('no_reviews')}</p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {reviews.map(review => (
                  <MemoizedReviewCard key={review.id} review={review} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
};
